

export interface TouchPoint {
  id: number;
  x: number; // Normalized 0-1
  y: number; // Normalized 0-1
  startY: number; // Initial y-position for pitch bend calculation
  pitchBend: number; // -1 to 1 for pitch bend amount
  pressure: number; // Raw pressure
  force: number; // Unified force (Pressure OR Contact Area)
  startTime: number; // Timestamp of creation
  endTime?: number; // Timestamp of release, for visual decay
}

export interface SonicPreset {
  name: string;
  description: string;
  baseFrequencies: number[];
  modulationFactor: number; // The 'k' constant
  resonance: number; // Filter resonance (Q factor)
  glideTime: number; // Pitch glide/portamento time in seconds
  colorPalette: {
    primary: string;
    secondary: string;
    background: string;
  };
  waveform: 'sine' | 'triangle' | 'sawtooth' | 'square' | 'piano' | 'electric-piano' | 'organ';
}

export interface AudioState {
  isPlaying: boolean;
  activeTouches: Map<number, TouchPoint>;
}

export type PlayMode = 'generative' | 'bach_bwv_924' | 'chopin_op9_no1' | 'bach_fantasia_g_minor' | 'chopin_op15_no2' | 'chopin_prelude_op45' | 'chopin_op9_no2' | 'chopin_prelude_op28_no4' | 'chopin_fantaisie_impromptu' | 'gershwin_rhapsody_in_blue' | 'liszt_la_campanella' | 'close_encounters_riff' | 'debussy_clair_de_lune' | 'chopin_minute_waltz' | 'mozart_rondo_alla_turca' | 'mozart_sonata_facile' | 'mozart_fantasia_d_minor' | 'mozart_twinkle_variations' | 'mozart_elvira_madigan' | 'beethoven_fur_elise' | 'beethoven_moonlight_sonata' | 'satie_gymnopedie_no1' | 'cinematic_interstellar' | 'cinematic_jaws' | 'cinematic_star_wars' | 'cinematic_james_bond' | 'cinematic_good_bad_ugly' | 'cinematic_mission_impossible' | 'cinematic_psycho' | 'cinematic_indiana_jones' | 'cinematic_pink_panther' | 'cinematic_harry_potter' | 'cinematic_halloween';

export type InteractionMode =
  | 'interferometer'
  | 'crystal'
  | 'gaussian'
  | 'euler'
  | 'chordal'
  | 'composer';

// Musical scale definitions for generative constraints
export type ScaleType =
  | 'chromatic'           // All 12 notes (no constraints)
  | 'major'               // Major scale
  | 'minor'               // Natural minor
  | 'pentatonic_major'    // Major pentatonic (5 notes)
  | 'pentatonic_minor'    // Minor pentatonic (5 notes)
  | 'blues'               // Blues scale
  | 'dorian'              // Dorian mode
  | 'phrygian'            // Phrygian mode
  | 'lydian'              // Lydian mode
  | 'mixolydian'          // Mixolydian mode
  | 'harmonic_minor'      // Harmonic minor
  | 'melodic_minor';      // Melodic minor

export type RootNote = 'C' | 'C#' | 'D' | 'D#' | 'E' | 'F' | 'F#' | 'G' | 'G#' | 'A' | 'A#' | 'B';

/**
 * Chord quality types
 */
export type ChordQuality = 'major' | 'minor' | 'diminished' | 'augmented' | 'dominant7' | 'minor7' | 'sus2' | 'sus4';

/**
 * Chord definition
 */
export interface Chord {
  root: number;        // MIDI note number for root
  quality: ChordQuality;
  notes: number[];     // Array of MIDI note numbers
}

export type QuantizationGrid = 'off' | '1/4' | '1/8' | '1/16' | '1/32';

export interface GenerativeConstraints {
  enabled: boolean;
  scale: ScaleType;
  rootNote: RootNote;
  octaveRange: { min: number; max: number }; // MIDI octave numbers (e.g., 2-5)
  quantization: QuantizationGrid;
  tempo: number; // BPM for quantization
}

export const DEFAULT_CONSTRAINTS: GenerativeConstraints = {
  enabled: true,
  scale: 'pentatonic_minor',
  rootNote: 'F',
  octaveRange: { min: 3, max: 5 },
  quantization: 'off',
  tempo: 120
};

export const DEFAULT_PRESET: SonicPreset = {
  name: "Solunn",
  description: "A pure, sine-wave based interference pattern.",
  baseFrequencies: [220, 261.63, 329.63, 392.00, 523.25], // A minor pentatonic ish
  modulationFactor: 50,
  resonance: 1, // Default Q factor (Cleaner for piano)
  glideTime: 0.1, // Default glide time
  colorPalette: {
    primary: "#00ffff",
    secondary: "#ff00ff",
    background: "#000000"
  },
  waveform: 'piano' // Default to piano
};


export interface AudioSettings {
  volume: number;      // 0-100
  reverb: number;      // 0-100
  delay: number;       // 0-100
  playMode: PlayMode;
  isGlideOn: boolean;
  constraints: GenerativeConstraints; // Generative mode constraints
  interactionMode: InteractionMode;
  // Atmosphere Settings
  masterTuning: 440 | 432;
  breathingMode: boolean;
  lofiMode: boolean;
  binauralMode: 'off' | 'alpha' | 'theta';
  voidMode: boolean;
  idleSoundsEnabled: boolean;
}

export const DEFAULT_AUDIO_SETTINGS: AudioSettings = {
  volume: 100, // Max volume default
  reverb: 5, // Was 0
  delay: 5, // Was 25
  playMode: 'bach_bwv_924',
  isGlideOn: false, // Was false
  constraints: DEFAULT_CONSTRAINTS,
  interactionMode: 'euler',
  masterTuning: 440,
  breathingMode: false,
  lofiMode: false,
  binauralMode: 'off',
  voidMode: false,
  idleSoundsEnabled: false
};
